import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  Copy,
  RefreshCw,
  UploadCloud,
  Loader2,
  Pencil,
  Check,
  Trash2,
  PlusCircle,
  ExternalLinkIcon
} from 'lucide-react';
import { assetService, type AssetWithPermission, type AssetVersionRecord, type MetadataItem } from '@/services/asset-service';

type AssetData = {
  uri?: string;
  dependencies?: Record<string, string>;
  [key: string]: unknown;
};

type Props = {
  asset: AssetWithPermission;
  onAssetUpdated: (asset: AssetWithPermission) => void;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const AssetDetailsPanel = ({ asset, onAssetUpdated }: Props) => {
  const parsedData: AssetData = useMemo(() => {
    if (isObject(asset.data)) {
      return asset.data as AssetData;
    }
    return {};
  }, [asset.data]);

  const dependencies = useMemo(() => {
    if (parsedData.dependencies && typeof parsedData.dependencies === 'object') {
      return Object.entries(parsedData.dependencies as Record<string, string>);
    }
    return [];
  }, [parsedData.dependencies]);

  const [nameInput, setNameInput] = useState(asset.name);
  const [isSavingName, setIsSavingName] = useState(false);

  const [mainFileDialogOpen, setMainFileDialogOpen] = useState(false);
  const [removeDependenciesOnReplace, setRemoveDependenciesOnReplace] = useState(false);
  const [selectedMainFile, setSelectedMainFile] = useState<File | null>(null);
  const [isReplacingMainFile, setIsReplacingMainFile] = useState(false);

  const [isUploadingDependency, setIsUploadingDependency] = useState(false);
  const [dependencyInProgress, setDependencyInProgress] = useState<string | null>(null);
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [isDeletingDependency, setIsDeletingDependency] = useState(false);

  const [newDependencyKey, setNewDependencyKey] = useState('');
  const [newDependencyFile, setNewDependencyFile] = useState<File | null>(null);
  const [isAddingDependency, setIsAddingDependency] = useState(false);

  const [versions, setVersions] = useState<AssetVersionRecord[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  const mainFileInputRef = useRef<HTMLInputElement | null>(null);
  const newDependencyInputRef = useRef<HTMLInputElement | null>(null);
  const dependencyFileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const loadVersions = useCallback(async () => {
    try {
      setVersionsLoading(true);
      const list = await assetService.listAssetVersions(asset.uuid);
      setVersions(list);
      setVersionsError(null);
    } catch (error) {
      console.error('Failed to load version history:', error);
      setVersionsError(error instanceof Error ? error.message : 'Failed to load version history');
    } finally {
      setVersionsLoading(false);
    }
  }, [asset.uuid]);

  const loadMetadata = useCallback(async () => {
    try {
      setMetadataLoading(true);
      const items = await assetService.listAssetMetadata(asset.uuid);
      setMetadata(items);
      setMetadataError(null);
    } catch (error) {
      console.error('Failed to load metadata:', error);
      setMetadataError(error instanceof Error ? error.message : 'Failed to load metadata');
    } finally {
      setMetadataLoading(false);
    }
  }, [asset.uuid]);

  const refreshAsset = useCallback(async () => {
    try {
      const updated = await assetService.getAsset(asset.uuid);
      onAssetUpdated({
        ...updated,
        permission: asset.permission
      });
      await Promise.all([loadVersions(), loadMetadata()]);
    } catch (error) {
      console.error('Failed to refresh asset details:', error);
      toast.error('Failed to refresh asset. Please reload the page.');
    }
  }, [asset.permission, asset.uuid, onAssetUpdated, loadVersions, loadMetadata]);

  const handleCopy = (value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success('Copied to clipboard'))
      .catch(() => toast.error('Failed to copy to clipboard'));
  };

  useEffect(() => {
    setNameInput(asset.name);
  }, [asset.name]);

  useEffect(() => {
    if (!mainFileDialogOpen) {
      setRemoveDependenciesOnReplace(false);
      setSelectedMainFile(null);
      if (mainFileInputRef.current) {
        mainFileInputRef.current.value = '';
      }
    }
  }, [mainFileDialogOpen]);

  useEffect(() => {
    if (renamingKey) {
      setRenameValue(renamingKey);
    } else {
    setRenameValue('');
  }
}, [renamingKey]);

  useEffect(() => {
    loadVersions();
    loadMetadata();
  }, [loadVersions, loadMetadata]);

  const formatTimestamp = useCallback((value: string | number | Date | null | undefined) => {
    if (!value) return 'Unknown';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString();
  }, []);

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      toast.error('Asset name cannot be empty');
      setNameInput(asset.name);
      return;
    }

    if (trimmed === asset.name) {
      toast.info('Asset name unchanged');
      return;
    }

    setIsSavingName(true);
    try {
      await assetService.updateAsset(asset.uuid, { name: trimmed });
      toast.success('Asset name updated');
      await refreshAsset();
    } catch (error) {
      console.error('Failed to update asset name:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update asset name');
      setNameInput(asset.name);
    } finally {
      setIsSavingName(false);
    }
  };

  const handleMainFileSelected: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedMainFile(file);
      setMainFileDialogOpen(true);
    }
    event.target.value = '';
  };

  const confirmReplaceMainFile = async () => {
    if (!selectedMainFile) {
      setMainFileDialogOpen(false);
      return;
    }
    setIsReplacingMainFile(true);
    try {
      await assetService.replaceAssetMainFile(asset.uuid, selectedMainFile, {
        removeDependencies: removeDependenciesOnReplace,
        name: nameInput.trim() || asset.name,
        type: asset.type
      });
      toast.success('Main file updated');
      await refreshAsset();
    } catch (error) {
      console.error('Failed to replace main file:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to replace main file');
    } finally {
      setIsReplacingMainFile(false);
      setSelectedMainFile(null);
      setRemoveDependenciesOnReplace(false);
      setMainFileDialogOpen(false);
    }
  };

  const handleDependencyFileSelected = async (
    key: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setDependencyInProgress(key);
    setIsUploadingDependency(true);
    try {
      await assetService.uploadDependencies(asset.uuid, [{ key, file }]);
      toast.success(`Dependency "${key}" updated`);
      await refreshAsset();
    } catch (error) {
      console.error('Failed to update dependency:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update dependency');
    } finally {
      setIsUploadingDependency(false);
      setDependencyInProgress(null);
    }
  };

  const handleRenameDependency = async () => {
    if (!renamingKey) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      toast.error('Dependency key cannot be empty');
      return;
    }
    if (trimmed === renamingKey) {
      toast.info('Dependency key unchanged');
      setRenamingKey(null);
      return;
    }
    setIsRenaming(true);
    try {
      await assetService.renameDependency(asset.uuid, renamingKey, trimmed);
      toast.success('Dependency renamed');
      await refreshAsset();
      setRenamingKey(null);
    } catch (error) {
      console.error('Failed to rename dependency:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to rename dependency');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleRemoveDependency = async (key: string) => {
    setDeletingKey(key);
    setIsDeletingDependency(true);
    try {
      await assetService.removeDependency(asset.uuid, key);
      toast.success(`Dependency "${key}" removed`);
      await refreshAsset();
    } catch (error) {
      console.error('Failed to remove dependency:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to remove dependency');
    } finally {
      setIsDeletingDependency(false);
      setDeletingKey(null);
    }
  };

  const handleAddDependency = async () => {
    const trimmedKey = newDependencyKey.trim();
    if (!trimmedKey) {
      toast.error('Please enter a dependency key');
      return;
    }
    if (!newDependencyFile) {
      toast.error('Please choose a file to upload');
      return;
    }

    setIsAddingDependency(true);
    try {
      await assetService.uploadDependencies(asset.uuid, [{ key: trimmedKey, file: newDependencyFile }]);
      toast.success('Dependency added');
      setNewDependencyKey('');
      setNewDependencyFile(null);
      if (newDependencyInputRef.current) {
        newDependencyInputRef.current.value = '';
      }
      await refreshAsset();
    } catch (error) {
      console.error('Failed to add dependency:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to add dependency');
    } finally {
      setIsAddingDependency(false);
    }
  };

  const handleNewDependencyFileSelected: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    setNewDependencyFile(file);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Asset Metadata</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
            <div className="space-y-1">
              <Label htmlFor="asset-name">Asset Name</Label>
              <Input
                id="asset-name"
                value={nameInput}
                onChange={(event) => setNameInput(event.target.value)}
                placeholder="Asset name"
                disabled={isSavingName}
              />
            </div>
            <Button onClick={handleSaveName} disabled={isSavingName}>
              {isSavingName ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Save
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Asset UUID</Label>
              <div className="mt-1 flex items-center justify-between rounded-md border bg-muted px-3 py-2 text-sm">
                <span className="truncate">{asset.uuid}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleCopy(asset.uuid)}
                  className="ml-2 h-8 w-8"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label>Asset Type</Label>
              <div className="mt-1">
                <Badge variant="secondary">{asset.type}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Main File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Main URI</Label>
            <div className="mt-1 flex items-center justify-between rounded-md border bg-muted px-3 py-2 text-sm">
              <span className="truncate">{parsedData.uri ?? 'Not available'}</span>
              {parsedData.uri && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleCopy(`${window.location.origin}${parsedData.uri}`)}
                  className="ml-2 h-8 w-8"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={mainFileInputRef}
              type="file"
              className="hidden"
              onChange={handleMainFileSelected}
            />
            <Button
              variant="outline"
              onClick={() => mainFileInputRef.current?.click()}
              disabled={isReplacingMainFile}
            >
              <UploadCloud className="mr-2 h-4 w-4" />
              Replace Main File
            </Button>
            <Button
              variant="outline"
              onClick={() => refreshAsset()}
              disabled={isReplacingMainFile}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Data
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Dependencies</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {dependencies.length === 0 ? (
            <p className="text-sm text-muted-foreground">This asset has no dependencies.</p>
          ) : (
            <div className="space-y-4">
              {dependencies.map(([key, uri]) => (
                <div key={key} className="rounded-lg border p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{key}</Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopy(`${window.location.origin}${uri}`)}
                        className="h-8 w-8"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="file"
                        className="hidden"
                        ref={(el) => {
                          dependencyFileInputs.current[key] = el;
                        }}
                        onChange={(event) => handleDependencyFileSelected(key, event)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => dependencyFileInputs.current[key]?.click()}
                        disabled={isUploadingDependency && dependencyInProgress === key}
                      >
                        {isUploadingDependency && dependencyInProgress === key ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <UploadCloud className="mr-2 h-4 w-4" />
                        )}
                        Replace File
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setRenamingKey(key);
                          setRenameValue(key);
                        }}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Rename
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveDependency(key)}
                        disabled={isDeletingDependency && deletingKey === key}
                      >
                        {isDeletingDependency && deletingKey === key ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="mr-2 h-4 w-4" />
                        )}
                        Remove
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-md border bg-muted px-3 py-2 text-sm">
                    <span className="break-all">{uri}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-lg border p-4">
            <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <PlusCircle className="h-4 w-4" />
              Add Dependency
            </h4>
            <div className="grid gap-3 md:grid-cols-[2fr_1fr_auto] md:items-end">
              <div className="space-y-1">
                <Label htmlFor="dependency-key">Dependency Key</Label>
                <Input
                  id="dependency-key"
                  placeholder="e.g. texture.png"
                  value={newDependencyKey}
                  onChange={(event) => setNewDependencyKey(event.target.value)}
                  disabled={isAddingDependency}
                />
              </div>
              <div className="space-y-1">
                <Label>File</Label>
                <div className="flex items-center gap-2">
                  <input
                    ref={newDependencyInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleNewDependencyFileSelected}
                  />
                  <Button
                    variant="outline"
                    onClick={() => newDependencyInputRef.current?.click()}
                    disabled={isAddingDependency}
                  >
                    <UploadCloud className="mr-2 h-4 w-4" />
                    Choose File
                  </Button>
                </div>
                {newDependencyFile && (
                  <p className="text-xs text-muted-foreground truncate">
                    Selected: {newDependencyFile.name}
                  </p>
                )}
              </div>
              <Button onClick={handleAddDependency} disabled={isAddingDependency}>
                {isAddingDependency ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Add Dependency
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Metadata</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {metadataLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : metadataError ? (
            <p className="text-sm text-destructive">{metadataError}</p>
          ) : metadata.length === 0 ? (
            <p className="text-sm text-muted-foreground">No metadata entries for this asset.</p>
          ) : (
            <div className="space-y-3">
              {metadata.map((item) => (
                <div key={item.id ?? `${item.assetUuid}-${item.key}`} className="rounded-lg border p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{item.key}</Badge>
                    </div>
                  </div>
                  <div className="rounded-md border bg-muted px-3 py-2 text-sm">
                    <span className="break-words whitespace-pre-wrap">{item.value ?? ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Version History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {versionsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : versionsError ? (
            <p className="text-sm text-destructive">{versionsError}</p>
          ) : versions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No versions recorded yet.</p>
          ) : (
            <div className="space-y-3">
              {versions.map((version) => {
                const dataObject = isObject(version.data) ? version.data as AssetData : null;
                const uri = dataObject && typeof dataObject.uri === 'string' ? dataObject.uri : undefined;
                const dependenciesCount =
                  dataObject && dataObject.dependencies && typeof dataObject.dependencies === 'object'
                    ? Object.keys(dataObject.dependencies as Record<string, string>).length
                    : 0;

                return (
                  <div
                    key={version.id ?? version.version}
                    className={`rounded-lg border p-4 space-y-3 ${
                      version.isCurrent ? 'border-primary/80 bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-sm font-semibold">Version {version.version}</h4>
                        <Badge variant={version.isCurrent ? 'default' : 'outline'}>
                          {version.isCurrent
                            ? 'Current'
                            : version.status.charAt(0).toUpperCase() + version.status.slice(1)}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Created {formatTimestamp(version.createdAt)}
                      </span>
                    </div>
                    <div className="grid gap-2 lg:grid-cols-2">
                      <div>
                        <Label className="text-xs uppercase text-muted-foreground">Main URI</Label>
                        <div className="mt-1 flex items-center justify-between rounded-md border bg-muted px-3 py-2 text-xs">
                          <span className="truncate">{uri ?? 'Not available'}</span>
                          {uri && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleCopy(`${window.location.origin}${uri}`)}
                              className="ml-2 h-8 w-8"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs uppercase text-muted-foreground">Dependencies</Label>
                        <div className="mt-1 rounded-md border bg-muted px-3 py-2 text-xs">
                          {dependenciesCount} file{dependenciesCount === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {uri && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`${window.location.origin}${uri}`, '_blank')}
                        >
                          <ExternalLinkIcon className="mr-2 h-4 w-4" />
                          Open URI
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleCopy(
                            JSON.stringify(version.data ?? {}, null, 2)
                          )
                        }
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copy Data JSON
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={mainFileDialogOpen} onOpenChange={setMainFileDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace main file?</AlertDialogTitle>
            <AlertDialogDescription>
              Re-uploading the main file can invalidate existing dependencies. Do you want to delete all dependencies after replacing the main file?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md border bg-muted px-3 py-2 text-sm">
            {selectedMainFile ? selectedMainFile.name : 'No file selected'}
          </div>
          <div className="flex items-center gap-2 rounded-md border px-3 py-2">
            <input
              id="remove-dependencies"
              type="checkbox"
              checked={removeDependenciesOnReplace}
              onChange={(event) => setRemoveDependenciesOnReplace(event.target.checked)}
            />
            <Label htmlFor="remove-dependencies" className="text-sm">
              Delete all dependencies after replacement
            </Label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setRemoveDependenciesOnReplace(false);
                setSelectedMainFile(null);
              }}
              disabled={isReplacingMainFile}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmReplaceMainFile} disabled={isReplacingMainFile}>
              {isReplacingMainFile ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UploadCloud className="mr-2 h-4 w-4" />
              )}
              Replace File
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={renamingKey !== null} onOpenChange={(open) => !open && setRenamingKey(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename dependency</AlertDialogTitle>
            <AlertDialogDescription>
              Update the key used to reference this dependency. Existing code that relies on the old key will need to be updated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="dependency-new-key">New Key</Label>
            <Input
              id="dependency-new-key"
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              disabled={isRenaming}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setRenamingKey(null)}
              disabled={isRenaming}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRenameDependency} disabled={isRenaming}>
              {isRenaming ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AssetDetailsPanel;
